/**
 * NOTE: CODEGEN IS USED IN THIS FILE. Careless manual edits may be lost.
 *
 * server/tools/syncDirs.ts keeps this file in sync with:
 *   server/src/shared/Types.ts
 */

/**
 * Contains some common type definitions and typebox helpers.
 */

import {Type, TSchema} from "@sinclair/typebox";
// import {ProjectConfig} from "../init/ProjectConfig";

/**
 * Typescript utility to set object keys to optional.
 * Example:
 *   type A = {x: number; y: number};
 *   type B = PartialBy<A, "x">; // {x?: number; y: number}
 *   type C = PartialBy<A, "x" | "y">; // {x?: number; y?: number}
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<T>;

/**
 * Typescript utility to set object keys to required. Opposite of PartialBy.
 * Example:
 *   type A = {x?: number; y?: number};
 *   type B = RequiredBy<A, "x">; // {x: number; y?: number}
 *   type C = RequiredBy<A, "x" | "y">; // {x: number; y: number}
 */
export type RequiredBy<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Typescript utility for arbitrary JSON. Based on
 * https://github.com/Microsoft/TypeScript/issues/3496#issuecomment-128553540
 */
export type JsonValue =
	| null
	| string
	| number
	| boolean
	| JsonObject
	| JsonArray;
export interface JsonObject {
	[x: string]: JsonValue;
}
export interface JsonArray extends Array<JsonValue> {}

/**
 * Typescript helper for verifying a type has been refined to never. Useful
 * for enums and discriminated unions to verify all cases are covered.
 *
 * At runtime, if called, it will call the optional fallback function and
 * then return undefined.
 *
 * @param value {never} The value to assert as never in Typescript.
 * @param fallback [function] The fallback function to call.
 * @returns {never}
 */
export function assertNever(_value: never, fallback?: () => void): never {
	typeof fallback === "function" && fallback();
	return void 0 as never;
}

export function assertIsJsonObject(value: JsonValue): value is JsonObject {
	return !!value && !Array.isArray(value) && typeof value === "object";
}

/**
 * Typebox helper for a type that can be one thing or null.
 */
export function Nullable<T extends TSchema>(t: T) {
	return Type.Union([Type.Null(), t]);
}

/**
 * Typebox Date type using the Facade machinery.
 */
export const dateT = Type.Unsafe<Date>({
	type: "object",
	customObjectType: "dateT",
});
export const jsonT = Type.Unsafe<JsonValue>(Type.Any());

/**
 * Some additional types specific to this project. (Maybe move later?)
 */

export const uuidT = Type.String({format: "uuid"});

export const usersT = Type.Object({
	id: uuidT,
	email: Type.Optional(Nullable(Type.String())),
	username: Type.Optional(Nullable(Type.String())),
	firstName: Type.Optional(Nullable(Type.String())),
	lastName: Type.Optional(Nullable(Type.String())),
	displayName: Type.Optional(Nullable(Type.String())),
	createdAt: Type.Optional(dateT),
	updatedAt: Type.Optional(dateT),
	isRestricted: Type.Optional(Type.Boolean()),
	isStaff: Type.Optional(Type.Boolean()),
	isNationalStaff: Type.Optional(Type.Boolean()),
	isTestAccount: Type.Optional(Type.Boolean()),
	emailVerifiedAt: Type.Optional(Nullable(dateT)),
	passwordSetAt: Type.Optional(Nullable(dateT)),
	mfaEnabledAt: Type.Optional(Nullable(dateT)),
	lastLoginAt: Type.Optional(Nullable(dateT)),
	lastActiveAt: Type.Optional(Nullable(dateT)),
	deactivatedAt: Type.Optional(Nullable(dateT)),
});

export type UsersRow = {
	id: string;
	email: string;
	username: string;
	firstName: string;
	lastName: string;
	displayName: string;
	createdAt: string;
	updatedAt: string;
	isRestricted: boolean;
	isStaff: boolean;
	isNationalStaff: boolean;
	isTestAccount: boolean;
	emailVerifiedAt: string;
	passwordSetAt: string;
	mfaEnabledAt: string;
	lastLoginAt: string;
	lastActiveAt: string;
	deactivatedAt: string;
};

// Type declaration for the return of getUser()
export type UserReturn = {
	user: UsersRow;
	hasMfaEnabled: boolean;
	applications: object[];
};

// Type declaration for the return of getNationalMfa()
export type MfaUserType = {
	id: string;
	email: string;
	username: string;
	firstName: string;
	lastName: string;
	enabledAt: string; // Originally a Date, but turns into string upon GET request
	mfaType: string;
};

// Type declaration for the return of getUserAccountHistory()
export type UserAccountActivityRow = {
	id: string;
	userId: string;
	targetUserId: string | null;
	activity: string | null;
	method: string | null;
	applicationCode: string | null;
	sessionId: string | null;
	userAgent: string | null;
	ip: string | null;
	data: object | null;
	createdAt: string;
	updatedAt: string;
	updatedBy: string | null;
};

// Type declaration for the return of getUserIpHistory()
export type UserIpHistoryRow = {
	loginCount: string;
	sessionCount: string;
	firstSeenAt: string;
	lastSeenAt: string;
	id: string;
	ip: string;
	userId: string;
};

// Type declaration for the return of getUserRelations(),
// which is a union of user_relations, user_relations_log, users, and user_applications
export type UserRelationsRow = {
	targetUserId: string;
	relationship: string;
	relationCreatedAt: string;
	logApplicationCode: string | null;
	reason: string | null;
	email: string | null;
	username: string | null;
	firstName: string | null;
	lastName: string | null;
	createdAt: string;
	applicationCode: string;
	applicationUserId: string | null;
};

// Type declaration for the return of getUserSessions()
export type UserSessionsRow = {
	id: string;
	userId: string;
	endedReason: string | null;
	endedBy: string | null;
	userAgent: string | null;
	ip: string | null;
	ipsSeen: string;
	createdAt: string;
	endedAt: string | null;
	expiresAt: string;
	lastActiveAt: string;
	lastMfaAt: string | null;
	lastUpdatedAt: string | null;
	imitatorUserId: string | null;
	trueImitation: string | null;
};

// Type declaration for the return of getUserApplications()
export type UserApplicationsRow = {
	id: string;
	userId: string;
	applicationCode: string;
	applicationUserId: string;
	initializedAt: string;
	lastActiveAt: string;
	createdAt: string;
	updatedAt: string;
};

// Type declaration for user search queries
export type UserWhere = {
	userId?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	username?: string;
	applicationCode?: string;
	applicationUserId?: string;
};

// Type declaration for the return of searchForUsers()
export type UserSearchReturn = UsersRow & {
	applicationUserId?: number;
	applicationUsername?: string;
};

/*
This isn't used for anything currently, and it breaks when copied to the nextjs dir
since there isn't a ProjectConfig there.
*/
// export interface legacyFetchParams {
// 	path: string;
// 	params: {[key: string]: string};
// 	projectConfig: ProjectConfig;
// 	method?: string;
// }
